import { useCallback, useState } from 'react';
import { getTiktokAudio, TiktokAudio } from '@round/api';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';

export default function useTiktokAudio(audioId: number | undefined) {
    const [tiktokAudio, setTiktokAudio] = useState<TiktokAudio | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAudio = useCallback(
        async (requestInit?: RequestInit) => {
            if (!audioId) {
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const response = await getTiktokAudio(audioId, requestInit);
                if (response.status === 404) {
                    setError(response.data.detail);
                    return;
                }

                setTiktokAudio(response.data);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setError((e as Error).message);
            } finally {
                setIsLoading(false);
            }
        },
        [audioId]
    );

    const abortController = useAbortableEffect(
        (signal) => {
            fetchAudio({ signal });
        },
        [fetchAudio]
    );

    const reset = useCallback(() => {
        setTiktokAudio(null);
        setIsLoading(false);
        setError(null);
    }, []);

    return {
        tiktokAudio,
        error,
        isLoading,
        abortController,
        reset,
    };
}
